import {RootState} from "../index";
import {Middleware} from "redux";
import _ from "lodash";
import axios, {AxiosHeaders, AxiosRequestConfig, RawAxiosRequestHeaders} from "axios";
import {AxiosResponse} from "axios";
import {env} from "configs/EnvironmentConfig";

export type onRequestSuccess = (response: AxiosResponse) => void;
export type onRequestFailure = (status: number, message: string) => void;
export type ApiRequestType = {
  type: 'API',
  url: string,
  method: 'GET'|'POST'|'PUT'|'DELETE',
  headers?: RawAxiosRequestHeaders | AxiosHeaders,
  slice: string,
  action: string,
  data?: any,
  onRequestSuccess?: onRequestSuccess,
  onRequestFailure?: onRequestFailure,
};

const API_BASE_URL= env.API_ENDPOINT_URL;

export const apiMiddleware: Middleware<{}, RootState> = storeApi => next => (action) => {

  const onRequestCreate = (request: ApiRequestType) => {
    storeApi.dispatch({
      type: `${request.slice.toLowerCase()}/onLoad${request.action.charAt(0).toUpperCase()+request.action.slice(1)}`,
      payload: {
        isError: true,
        errorCode: 0,
        errorMessage: null,
      }
    })
  }

  const onRequestSuccess= (request: ApiRequestType, data: any) => {
    storeApi.dispatch({
      type: `${request.slice.toLowerCase()}/onSuccess${request.action.charAt(0).toUpperCase()+request.action.slice(1)}`,
      payload: {
        data: data,
        isError: false,
        errorCode: 0,
        errorMessage: null,
      }
    });
  }

  const onRequestError = (request: ApiRequestType, errorCode: number, errorMessage: string) => {
    storeApi.dispatch({
      type: `${request.slice.toLowerCase()}/onError${request.action.charAt(0).toUpperCase()+request.action.slice(1)}`,
      payload: {
        isError: true,
        errorCode: errorCode,
        errorMessage: errorMessage,
      }
    });
  };

  const isValidApiRequest = (request: any) => {
    return !_.isNil(action)
      && _.isObject(action)
      && 'type' in action
      && _.isString(action.type)
      && action.type.toUpperCase() === 'API';
  }

  const setPagination = ( response: AxiosResponse) : void => {
    if( _.has(response.data, 'current_page') && _.isNumber(response.data.current_page) &&
      _.has(response.data, 'per_page') &&  _.isNumber(response.data.per_page) &&
      _.has(response.data, 'total') &&  _.isNumber(response.data.total) &&
      _.has(response.data, 'last_page') &&  _.isNumber(response.data.last_page)) {

      storeApi.dispatch({type: 'pagination/setPageTotal', payload: response.data.total});
      storeApi.dispatch({type: 'pagination/setPageSize', payload: response.data.per_page});

      if( response.data.current_page > response.data.last_page ) {
        storeApi.dispatch({type: 'pagination/setPageCurrent', payload: response.data.last_page});
      } else {
        storeApi.dispatch({type: 'pagination/setPageCurrent', payload: response.data.current_page});
      }
    }
  };

  const main = () => {
    if( !isValidApiRequest(action) ) {
      return next(action);
    }

    const request: ApiRequestType = action as ApiRequestType;

    const dataType: string = ['GET', 'DELETE'].includes(request.method) ? 'params' : 'data';

    const axiosRequestConfig: AxiosRequestConfig = {
      url: request.url,
      method: request.method,
      baseURL: `${API_BASE_URL}`,
      headers: {
        ...request.headers,
        Accept: 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
      timeout: 0,
      withCredentials: false,
      responseType: 'json',
      responseEncoding: 'utf8',
      xsrfCookieName: 'XSRF-TOKEN',
      xsrfHeaderName: 'X-XSRF-TOKEN',
      maxRedirects: 5,
      socketPath: null,
      decompress: true,
      [dataType]: request.data,
    };

    const reduxState = storeApi.getState();
    if( reduxState ) {

      // Pagination
      if( reduxState.pagination ) {
        axiosRequestConfig.headers = {
          ...axiosRequestConfig.headers,
          'Page-Current': reduxState.pagination.pageCurrent,
          'Page-Size': reduxState.pagination.pageSize,
        }
      }

      // Sorting
      if( reduxState.sort ) {
        axiosRequestConfig.headers = {
          ...axiosRequestConfig.headers,
          'Sort-Column': reduxState.sort.sortColumn,
          'Sort-Order': reduxState.sort.sortOrder,
        }
      }

      // Filtering
      if( reduxState.filter ) {

        axiosRequestConfig.headers = {
          ...axiosRequestConfig.headers,
          // @ts-ignore
          'Filter-Column': encodeURI(reduxState.filter.filterColumn),

          // @ts-ignore
          'Filter-Value': encodeURI(reduxState.filter.filterValue),
        }
      }
    }


    onRequestCreate(request);
    axios.request(axiosRequestConfig).then((response:AxiosResponse)=>{
      setPagination(response);

      onRequestSuccess(request, response.data);
      if( typeof request.onRequestSuccess === 'function' ) {
        request.onRequestSuccess(response.data);
      }

    }).catch( (error: unknown) => {

      if( axios.isAxiosError(error) ) {
        if( error.response ) {
          onRequestError(request, error.response.status, error.response.statusText);
          if( typeof request.onRequestFailure === 'function' ) {
            request.onRequestFailure(error.response.status, error.response.statusText);
          }
        } else if( error.request ) {
          onRequestError(request, -1, 'No response received');
          if( typeof request.onRequestFailure === 'function' ) {
            request.onRequestFailure(-1, 'No response received');
          }
        } else {
          onRequestError(request, error.status||-1, error.message);
          if( typeof request.onRequestFailure === 'function' ) {
            request.onRequestFailure(error.status||-1, error.message);
          }
        }
      } else {
        onRequestError(request, -1, 'An unexpected error occurred');
      }
    });
  }

  main();
}